/**
 * Signing in with Firebase.
 *
 * Nothing here runs unless the backend asks for it. In DEV identity mode `/api/me` answers
 * without a token, the gate never reaches this module, and the SDK is never initialised —
 * which is what keeps local development free of a sign-in step it does not need.
 *
 * The configuration is fetched from `/__/firebase/init.json`, which Firebase Hosting serves
 * for the project the site is deployed to. That is deliberate: the alternative is build-time
 * `VITE_FIREBASE_*` variables, which would mean the bundle is built for one project and the
 * repository carries a second copy of values Terraform already owns. The web config is public
 * by design — an API key here identifies the project, it does not authorise anything.
 */
import {
  GoogleAuthProvider,
  getAuth,
  onIdTokenChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from 'firebase/auth'
import { initializeApp, type FirebaseApp } from 'firebase/app'

type InitJson = {
  apiKey: string
  authDomain: string
  projectId: string
  appId?: string
}

let app: FirebaseApp | null = null
let auth: Auth | null = null

/** The token the API client attaches. Kept here so reading it costs nothing per request. */
let currentToken: string | null = null

/**
 * Initialises the SDK, once, and resolves when the first auth state is known.
 *
 * Waiting for that first callback matters: Firebase restores a persisted session
 * asynchronously, so a gate that rendered immediately would show a sign-in button to
 * somebody who is already signed in, for as long as the restore takes.
 */
export async function initAuth(): Promise<Auth> {
  if (auth) return auth

  const response = await fetch('/__/firebase/init.json')
  if (!response.ok) {
    throw new Error(
      `Firebase sign-in is configured on the server but its web config is not available ` +
        `(/__/firebase/init.json returned ${response.status}). Firebase Hosting serves that ` +
        `file; outside Hosting there is nothing to serve it.`,
    )
  }
  const config = (await response.json()) as InitJson

  app = initializeApp(config)
  auth = getAuth(app)

  await new Promise<void>((resolve) => {
    let settled = false
    onIdTokenChanged(auth!, (user) => {
      // onIdTokenChanged fires again whenever the SDK refreshes, roughly hourly, which is
      // what keeps currentToken from going stale without anything polling for it.
      void refreshToken(user)
      if (!settled) {
        settled = true
        resolve()
      }
    })
  })

  return auth
}

async function refreshToken(user: User | null) {
  currentToken = user ? await user.getIdToken() : null
}

/** The current ID token, or null when nobody is signed in. */
export function idToken(): string | null {
  return currentToken
}

export function currentUser(): User | null {
  return auth?.currentUser ?? null
}

export async function signIn(): Promise<void> {
  const instance = await initAuth()
  const provider = new GoogleAuthProvider()
  const credential = await signInWithPopup(instance, provider)
  await refreshToken(credential.user)
}

export async function signOut(): Promise<void> {
  if (!auth) return
  await firebaseSignOut(auth)
  currentToken = null
}
